import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { useInventoryViewStore as useInventoryStore, isInventoryPracticeActive, isCustomerInventoryPractice } from './practice/inventoryPractice';
import {
  leakageIngredientTarget,
  leakageSetupDish,
  leakageSetupQuery,
  leakageWalkthroughRowUid,
  relinkMatchProduct,
  relinkSearchQuery,
  setupTargetSubrecipe,
  resetWalkthroughSnapshots,
  snapshotWalkthroughAddProduct,
  snapshotWalkthroughCount,
  snapshotWalkthroughSection,
  walkthroughAddAlreadyCounted,
  walkthroughAddCount,
  walkthroughAddProduct,
  walkthroughCountProduct,
  walkthroughCountTarget,
  walkthroughDrillGroup,
  walkthroughLeakageAltBaselineKey,
  walkthroughLeakageAltSessionId,
  walkthroughSection,
  leakageHasNegativeRows,
} from './inventoryValues';

let actionGeneration = 0;
const pending = new Set<number>();
export function cancelInventoryActions() { actionGeneration++; leakageSetupContinueLocked = false; pending.forEach(id => window.clearTimeout(id)); pending.clear(); }
function schedule(callback: () => void, delay: number) {
  const generation = actionGeneration;
  const id = window.setTimeout(() => { pending.delete(id); if (generation === actionGeneration && isInventoryPracticeActive()) callback(); }, delay);
  pending.add(id); return id;
}
if (typeof window !== 'undefined') window.addEventListener('onboarding:inventory-cancel-actions', cancelInventoryActions);
function setPracticeLeakageQtyUnit(unit: 'count' | 'std') { dispatch('onboarding:inventory-leakage-unit', { unit }); }

function dispatch(name: string, detail?: Record<string, string>) {
  if (isInventoryPracticeActive()) window.dispatchEvent(new CustomEvent(name, { detail }));
}

let leakageFillGen = 0;

export function ensureRestaurantSelected() { return isInventoryPracticeActive(); }

export function startInventoryWalkthrough() {
  resetWalkthroughSnapshots();
  ensureRestaurantSelected();
}

export function showCountReviewTab() {
  ensureRestaurantSelected();
  dispatch('onboarding:inventory-tab', { tab: 'review' });
}

export function showLeakageTab() {
  ensureRestaurantSelected();
  if (isCustomerInventoryPractice()) { dispatch('onboarding:inventory-tab', { tab: 'variance' }); return; }
  const group = walkthroughDrillGroup();
  const selectedId = useInventoryStore.getState().countReview.selection.groupId;
  if (group && group.id !== selectedId) {
    dispatch('onboarding:inventory-select-group', { groupId: group.id, tab: 'variance' });
  } else {
    dispatch('onboarding:inventory-tab', { tab: 'variance' });
  }
}

export function showLeakageOverview() {
  showLeakageTab();
  restoreLeakageLatestSession();
  dispatch('onboarding:inventory-leakage-reset');
  void setPracticeLeakageQtyUnit('count');
}

export function openLeakageDates() {
  showLeakageOverview();
}

export function showLeakagePairPicker() {
  showLeakageTab();
  schedule(() => dispatch('onboarding:inventory-open-sessions'), 80);
}

export function applyLeakageAltPair() {
  showLeakageTab();
  const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
  const group = walkthroughDrillGroup();
  const sessionId = walkthroughLeakageAltSessionId();
  if (restaurantId && group && sessionId) {
    useInventoryStore.getState().loadCountReview(restaurantId, { groupId: group.id, sessionId });
    dispatch('onboarding:inventory-close-sessions');
    return;
  }
  const baselineKey = walkthroughLeakageAltBaselineKey();
  if (baselineKey) dispatch('onboarding:inventory-leakage-baseline', { key: baselineKey });
  dispatch('onboarding:inventory-close-sessions');
}

export function restoreLeakageLatestSession() {
  if (isCustomerInventoryPractice()) { dispatch('onboarding:inventory-close-sessions'); return; }
  const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
  const group = walkthroughDrillGroup();
  if (restaurantId && group) {
    useInventoryStore.getState().loadCountReview(restaurantId, { groupId: group.id, sessionId: null });
  }
  dispatch('onboarding:inventory-close-sessions');
}

export function showLeakageTopLoss() {
  showLeakageTab();
  restoreLeakageLatestSession();
  dispatch('onboarding:inventory-leakage-reset');
  void setPracticeLeakageQtyUnit('count');
}

export function filterLeakageNegative() {
  showLeakageTab();
  dispatch('onboarding:inventory-leakage-dir', { dir: isCustomerInventoryPractice() && !leakageHasNegativeRows() ? 'all' : 'negative' });
}

export function switchLeakageStdUnits() {
  filterLeakageNegative();
  void setPracticeLeakageQtyUnit('std');
}

/** Tab + filters without tearing down an open drawer — remounting was the inventory flicker. */
function ensureLeakageProductContext() {
  filterLeakageNegative();
  void setPracticeLeakageQtyUnit('std');
}

export function openLeakageProduct() {
  ensureLeakageProductContext();
  if (document.querySelector('[data-onboarding~="inventory-leakage-drawer"]')) return;
  const tryOpen = (attempt: number) => {
    schedule(() => {
      if (document.querySelector('[data-onboarding~="inventory-leakage-drawer"]')) return;
      dispatch('onboarding:inventory-leakage-open-row', { uid: leakageWalkthroughRowUid() ?? '' });
      document.querySelector<HTMLElement>('[data-onboarding~="inventory-leakage-row"]')?.click();
      if (attempt < 16) tryOpen(attempt + 1);
    }, attempt === 0 ? 240 : 160);
  };
  tryOpen(0);
}

export function showLeakageAddLink() {
  openLeakageProduct();
  const tryScroll = (attempt: number) => {
    schedule(() => {
      const btn = document.querySelector<HTMLElement>('[data-onboarding~="inventory-leakage-add-link"]');
      btn?.scrollIntoView({ block: 'center', inline: 'nearest' });
      if (btn || attempt >= 16) return;
      tryScroll(attempt + 1);
    }, attempt === 0 ? 280 : 160);
  };
  tryScroll(0);
}

export function openLeakageAddLink() {
  if (document.querySelector('[data-onboarding~="inventory-setup"]')) return;
  showLeakageAddLink();
  const tryOpen = (attempt: number) => {
    schedule(() => {
      if (document.querySelector('[data-onboarding~="inventory-setup"]')) return;
      dispatch('onboarding:inventory-leakage-add-link');
      document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-leakage-add-link"]')?.click();
      if (attempt < 16) tryOpen(attempt + 1);
    }, attempt === 0 ? 400 : 180);
  };
  tryOpen(0);
}

export function showLeakageSetupRevenue() {
  if (!document.querySelector('[data-onboarding~="inventory-setup"]')) {
    openLeakageAddLink();
  }
  schedule(() => dispatch('onboarding:inventory-setup-tab', { tab: 'revenue' }), 80);
}

export function searchLeakageSetupDish() {
  const setupOpen = Boolean(document.querySelector('[data-onboarding~="inventory-setup"]'));
  if (!setupOpen) {
    showLeakageSetupRevenue();
  } else {
    dispatch('onboarding:inventory-setup-tab', { tab: 'revenue' });
  }
  schedule(
    () => dispatch('onboarding:inventory-setup-search', { query: leakageSetupQuery() }),
    setupOpen ? 80 : 500
  );
}

/** Prevents StrictMode double-invokes from clicking Continue more than once. */
let leakageSetupContinueLocked = false;

function pickLeakageSetupDishOnce() {
  const name = leakageSetupDish();
  if (name) dispatch('onboarding:inventory-setup-select', { name, kind: 'revenue' });

}

function trySelectLeakageSetupDish() {
  const continueBtn = document.querySelector<HTMLButtonElement>(
    '[data-onboarding~="inventory-setup-continue"]'
  );
  if (continueBtn && !continueBtn.disabled) return;
  if (!document.querySelector('[data-onboarding~="inventory-setup"]')) {
    searchLeakageSetupDish();
  } else {
    dispatch('onboarding:inventory-setup-search', { query: leakageSetupQuery() });
  }
  const trySelect = (attempt: number) => {
    schedule(() => {
      const ready = document.querySelector<HTMLButtonElement>(
        '[data-onboarding~="inventory-setup-continue"]'
      );
      if (ready && !ready.disabled) return;
      pickLeakageSetupDishOnce();
      if (attempt < 16) trySelect(attempt + 1);
    }, attempt === 0 ? 400 : 160);
  };
  trySelect(0);
}

export function selectLeakageSetupDish() {
  leakageSetupContinueLocked = false;
  trySelectLeakageSetupDish();
}

export function continueLeakageSetup() {
  if (leakageSetupContinueLocked) return;
  leakageSetupContinueLocked = true;
  let clicked = false;
  const tryContinue = (attempt: number) => {
    schedule(() => {
      if (
        document.querySelector('[data-onboarding~="inventory-setup-done"]') ||
        document.querySelector('[data-onboarding~="cost-detail-panel"]')
      ) {
        return;
      }
      if (attempt >= 24) {
        leakageSetupContinueLocked = false;
        return;
      }
      if (clicked) {
        tryContinue(attempt + 1);
        return;
      }
      if (!document.querySelector('[data-onboarding~="inventory-setup"]')) {
        openLeakageAddLink();
        tryContinue(attempt + 1);
        return;
      }
      const continueBtn = document.querySelector<HTMLButtonElement>(
        '[data-onboarding~="inventory-setup-continue"]'
      );
      if (continueBtn) {
        if (continueBtn.disabled) {
          pickLeakageSetupDishOnce();
          tryContinue(attempt + 1);
          return;
        }
        dispatch('onboarding:inventory-setup-continue');

        clicked = true;
        tryContinue(attempt + 1);
        return;
      }
      tryContinue(attempt + 1);
    }, attempt === 0 ? 200 : 180);
  };
  tryContinue(0);
}

export function showLeakageLinkedDish() {
  const tryDone = (attempt: number) => {
    schedule(() => {
      const done = document.querySelector<HTMLButtonElement>(
        '[data-onboarding~="inventory-setup-done"]'
      );
      if (done) {
        done.click();
        dispatch('onboarding:inventory-leakage-close-setup');
        schedule(() => openLeakageProduct(), 240);
        return;
      }
      if (document.querySelector('[data-onboarding~="cost-detail-panel"]')) {
        dispatch('onboarding:inventory-leakage-close-setup');
        return;
      }
      const continueBtn = document.querySelector<HTMLButtonElement>(
        '[data-onboarding~="inventory-setup-continue"]'
      );
      if (continueBtn) {
        if (attempt === 0) continueLeakageSetup();
        if (attempt < 20) tryDone(attempt + 1);
        return;
      }
      // Wait while setup is still mounting — do not close mid-flight.
      if (document.querySelector('[data-onboarding~="inventory-setup"]') && attempt < 20) {
        tryDone(attempt + 1);
        return;
      }
      openLeakageProduct();
    }, attempt === 0 ? 400 : 220);
  };
  tryDone(0);
}

export function openLeakageDishEdit() {
  if (document.querySelector('[data-onboarding~="cost-detail-panel"]')) return;
  if (!document.querySelector('[data-onboarding~="inventory-leakage-drawer"]')) {
    showLeakageLinkedDish();
  } else if (document.querySelector('[data-onboarding~="inventory-setup"]')) {
    showLeakageLinkedDish();
  }
  const tryEdit = (attempt: number) => {
    schedule(() => {
      if (document.querySelector('[data-onboarding~="cost-detail-panel"]')) return;
      document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-leakage-edit-dish"]')?.click();
      dispatch('onboarding:inventory-leakage-open-edit');
      if (attempt < 20) tryEdit(attempt + 1);
    }, attempt === 0 ? 600 : 220);
  };
  tryEdit(0);
}

export function fillLeakageIngredientQty() {
  const gen = ++leakageFillGen;
  if (!document.querySelector('[data-onboarding~="cost-detail-panel"]')) {
    openLeakageDishEdit();
  }
  const tryFill = (attempt: number) => {
    schedule(() => {
      if (gen !== leakageFillGen) return;
      const target = leakageIngredientTarget();
      const input =
        document.querySelector<HTMLInputElement>(
          '[data-onboarding~="cost-detail-panel"] [data-onboarding~="inventory-leakage-ing-qty"]'
        ) ??
        document.querySelector<HTMLInputElement>('[data-onboarding~="inventory-leakage-ing-qty"]');
      if (input && target.qty && input.value === target.qty) return;
      if (input && target.qty) {
        const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        setter?.call(input, target.qty);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.blur();
      }
      if (target.name && target.qty) {
        dispatch('onboarding:practice-ingredient-quantity', { name: target.name, qty: target.qty });
      }
      if (attempt < 16) tryFill(attempt + 1);
    }, attempt === 0 ? 200 : 180);
  };
  tryFill(0);
}

export function showLeakageCharts() {
  leakageFillGen += 1;
  dispatch('onboarding:inventory-leakage-close-all');
  void setPracticeLeakageQtyUnit('count');
  dispatch('onboarding:inventory-category-mode', { mode: 'inventory' });
  showLeakageTab();
  schedule(() => {
    dispatch('onboarding:inventory-leakage-close-all');
    showLeakageTab();
    document
      .querySelector<HTMLElement>('[data-onboarding~="inventory-leakage-categories"]')
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, 280);
}

export function showLeakageSectionChart() {
  showLeakageCharts();
  schedule(() => {
    document
      .querySelector<HTMLElement>('[data-onboarding~="inventory-leakage-sections"]')
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, 360);
}

export function showInventoryCategories() {
  showCountReviewTab();
  dispatch('onboarding:inventory-category-mode', { mode: 'inventory' });
}

export function showProductCategories() {
  showCountReviewTab();
  dispatch('onboarding:inventory-category-mode', { mode: 'product' });
}

export function showAttentionNotLinked() {
  showCountReviewTab();
  dispatch('onboarding:inventory-attention-tab', { tab: 'notLinked' });
}

export function openRelinkUnlinked() {
  showAttentionNotLinked();
  dispatch('onboarding:inventory-open-relink');
}

export function showRelinkOrderProducts() {
  openRelinkUnlinked();
  schedule(() => dispatch('onboarding:inventory-relink-tab', { tab: 'products' }), 160);
}

export function searchRelinkMatch() {
  showRelinkOrderProducts();
  schedule(() => dispatch('onboarding:inventory-relink-search', { query: relinkSearchQuery() }), 280);
}

export function selectRelinkMatch() {
  searchRelinkMatch();
  const trySelect = (attempt: number) => {
    schedule(() => {
      const match = relinkMatchProduct();
      if (match) {
        dispatch('onboarding:inventory-relink-select', { name: match.productName });
        return;
      }
      if (attempt < 12) trySelect(attempt + 1);
    }, attempt === 0 ? 500 : 120);
  };
  trySelect(0);
}

export function confirmRelinkMatch() {
  const btn = document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-relink-confirm"]');
  if (btn && !btn.disabled && !btn.hasAttribute('data-saving')) {
    dispatch('onboarding:inventory-relink-confirm');
  }
  // Close immediately so the clear card matches Figma (empty table, no drawer).
  // The save keeps running in the background from the confirm handler.
  schedule(() => {
    dispatch('onboarding:inventory-close-relink');
    showAttentionNotLinked();
  }, 80);
}

export function showAttentionNotSetup() {
  showCountReviewTab();
  dispatch('onboarding:inventory-attention-tab', { tab: 'notSetup' });
}

export function openSetupNotSetup() {
  showAttentionNotSetup();
  if (!document.querySelector('[data-onboarding~="inventory-setup"]')) {
    dispatch('onboarding:inventory-open-setup');
  }
}

export function showSetupSubrecipes() {
  openSetupNotSetup();
  schedule(() => dispatch('onboarding:inventory-setup-tab', { tab: 'subrecipe' }), 200);
}

export function selectSetupTarget() {
  showSetupSubrecipes();
  const trySelect = (attempt: number) => {
    schedule(() => {
      const target = setupTargetSubrecipe();
      if (target) {
        dispatch('onboarding:inventory-setup-select', { name: target.name, id: target.id, subrecipeId: target.id });
        return;
      }
      if (attempt < 12) trySelect(attempt + 1);
    }, attempt === 0 ? 400 : 120);
  };
  trySelect(0);
}

export function continueSetupTarget() {
  selectSetupTarget();
  schedule(() => dispatch('onboarding:inventory-setup-continue'), 700);
}

export function confirmSetupSave() {
  const trySave = (attempt: number) => {
    const drawer = document.querySelector('[data-onboarding~="inventory-setup"]');
    if (!drawer) {
      showAttentionNotSetup();
      return;
    }
    const save = document.querySelector<HTMLButtonElement>('[data-onboarding~="cost-subrecipe-save"]');
    if (save?.disabled) {
      if (attempt < 20) schedule(() => trySave(attempt + 1), 250);
      return;
    }
    if (save) {
      save.click();
      schedule(() => trySave(attempt + 1), 250);
      return;
    }
    if (attempt === 0) continueSetupTarget();
    if (attempt < 20) schedule(() => trySave(attempt + 1), 250);
  };
  trySave(0);
}

export function showAllInventories() {
  showCountReviewTab();
  dispatch('onboarding:inventory-select-group', { groupId: '' });
}

export function openWalkthroughGroup() {
  if (isCustomerInventoryPractice()) { showCountReviewTab(); return; }
  const group = walkthroughDrillGroup();
  showCountReviewTab();
  if (group) dispatch('onboarding:inventory-select-group', { groupId: group.id });
}

export function showAttentionNeedsReview() {
  openWalkthroughGroup();
  dispatch('onboarding:inventory-attention-tab', { tab: 'needsReview' });
}

export function openWalkthroughSection() {
  snapshotWalkthroughSection();
  const section = walkthroughSection();
  const group = walkthroughDrillGroup();
  openWalkthroughGroup();
  if (!section) return;
  void useInventoryStore.getState().loadSectionProducts(section.id).then(() => {
    snapshotWalkthroughAddProduct();
  });
  dispatch('onboarding:inventory-open-section', {
    sectionId: section.id,
    groupId: group?.id ?? '',
  });
}

export function showSectionProductsPreview() {
  openWalkthroughSection();
  schedule(() => dispatch('onboarding:inventory-section-view-all', { full: 'false' }), 120);
}

export function expandSectionViewAll() {
  snapshotWalkthroughCount();
  openWalkthroughSection();
  schedule(() => {
    dispatch('onboarding:inventory-section-view-all', { full: 'true' });
    dispatch('onboarding:inventory-section-mode', { mode: 'counts' });
  }, 120);
}

export function openWalkthroughCountEdit() {
  snapshotWalkthroughCount();
  expandSectionViewAll();
  const tryOpen = (attempt: number) => {
    schedule(() => {
      if (document.querySelector('[data-onboarding~="inventory-section-count-input"]')) return;
      const btn = document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-section-count"]');
      if (btn) {
        btn.click();
        return;
      }
      dispatch('onboarding:inventory-open-count-edit');
      if (attempt < 20) tryOpen(attempt + 1);
    }, attempt === 0 ? 240 : 120);
  };
  tryOpen(0);
}

export function applyWalkthroughCount() {
  snapshotWalkthroughCount();
  expandSectionViewAll();
  const product = walkthroughCountProduct();
  const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
  const target = walkthroughCountTarget();
  if (product && restaurantId && product.count !== target) {
    void useInventoryStore.getState().updateCountQuantities(product.countId, restaurantId, [
      { productKey: product.productKey, count: target },
    ]);
  }
}

export function showSectionQuantityMode() {
  openWalkthroughSection();
  schedule(() => {
    dispatch('onboarding:inventory-section-view-all', { full: 'true' });
    dispatch('onboarding:inventory-section-mode', { mode: 'quantity' });
  }, 120);
}

export function openSectionAddProduct() {
  showSectionQuantityMode();
  const section = walkthroughSection();
  const load = section ? useInventoryStore.getState().loadSectionProducts(section.id) : Promise.resolve();
  void Promise.resolve(load).finally(() => {
    snapshotWalkthroughAddProduct();
    dispatch('onboarding:inventory-section-add-product');
  });
}

export function selectAddWalkthroughProduct() {
  if (!document.querySelector('[data-onboarding~="inventory-add-product"]')) {
    openSectionAddProduct();
  }
  const trySelect = (attempt: number) => {
    schedule(() => {
      const product = walkthroughAddProduct();
      if (product) {
        dispatch('onboarding:inventory-add-product-select', { name: product.productName, key: product.productKey });
        const row = document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-add-product-pick"]');
        if (row) {
          row.click();
          return;
        }
      }
      if (attempt < 16) trySelect(attempt + 1);
    }, attempt === 0 ? 400 : 150);
  };
  trySelect(0);
}

export function fillAddWalkthroughCount() {
  selectAddWalkthroughProduct();
  schedule(() => {
    dispatch('onboarding:inventory-add-product-count', { count: String(walkthroughAddCount()) });
  }, 700);
}

export function confirmAddWalkthroughProduct() {
  if (walkthroughAddAlreadyCounted()) {
    dispatch('onboarding:inventory-close-add-product');
    return;
  }
  if (!document.querySelector('[data-onboarding~="inventory-add-product"]')) {
    fillAddWalkthroughCount();
  } else {
    dispatch('onboarding:inventory-add-product-count', { count: String(walkthroughAddCount()) });
  }
  const trySave = (attempt: number) => {
    schedule(() => {
      if (walkthroughAddAlreadyCounted()) {
        dispatch('onboarding:inventory-close-add-product');
        return;
      }
      const btn = document.querySelector<HTMLButtonElement>('[data-onboarding~="inventory-add-product-confirm"]');
      if (btn && !btn.disabled) {
        btn.click();
        return;
      }
      if (attempt < 20) trySave(attempt + 1);
    }, attempt === 0 ? 900 : 200);
  };
  trySave(0);
}

export function showAddedWalkthroughProduct() {
  if (!walkthroughAddAlreadyCounted()) confirmAddWalkthroughProduct();
  else dispatch('onboarding:inventory-close-add-product');
  schedule(() => showSectionQuantityMode(), 500);
}

export function closeAddWalkthroughProduct() {
  dispatch('onboarding:inventory-close-add-product');
}
